/**
 * Client-side arithmetic formula evaluator.
 *
 * Syntax:
 *   - `+`, `-`, `*`, `/`, parentheses, decimal numbers
 *   - bare identifiers (e.g. `menge`) resolved via `context.identifiers`
 *   - bracket references (e.g. `[Artikelnummer]`) resolved via `context.docCells`
 *     — lets helper formulas pull raw document-cell values by column header
 *
 * All references resolve to numbers; non-numeric or missing values surface as a
 * localized error string on the result.  Helpers that need string output can
 * layer a second pass on top; this evaluator is intentionally narrow so the
 * same syntax stays compatible with the backend `formula.py` evaluator for
 * persisted formula primitives.
 */

import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

export interface FormulaContext {
  /** Bare-identifier lookups (primitives, helpers, rollups). */
  identifiers: Record<string, string | null | undefined>
  /** [bracket]-lookups for raw document cell values keyed by header. */
  docCells?: Record<string, string | null | undefined>
}

export interface FormulaResult {
  value: string | null
  error: string | null
}

/** Minimal translate() signature — mirrors useTranslation()'s translate(). */
export type TranslateFn = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

/** Evaluate a formula expression against the given context. */
export function evaluateFormula(
  expression: string,
  ctx: FormulaContext,
  translate: TranslateFn,
): FormulaResult {
  if (!expression || !expression.trim())
    return { value: null, error: translate("QMS/FORMULA/ERR_EMPTY") }

  // --- Pass 1: substitute [bracket] doc-cell references.
  const bracketPattern = /\[([^\]]+)\]/g
  let expr = expression
  let m: RegExpExecArray | null
  const bracketMatches: { raw: string; header: string }[] = []
  while ((m = bracketPattern.exec(expression)) !== null) {
    bracketMatches.push({ raw: m[0], header: m[1].trim() })
  }
  for (const { raw, header } of bracketMatches) {
    const docs = ctx.docCells ?? {}
    const cell = docs[header]
    if (cell == null || cell === "") {
      return {
        value: null,
        error: translate("QMS/FORMULA/ERR_MISSING_DOC_VALUE", { header }),
      }
    }
    const num = Number(String(cell).replace(/,/g, "."))
    if (isNaN(num)) {
      return {
        value: null,
        error: translate("QMS/FORMULA/ERR_NON_NUMERIC_DOC_VALUE", {
          header,
          cell: String(cell),
        }),
      }
    }
    expr = expr.split(raw).join(String(num))
  }

  // --- Pass 2: substitute bare-identifier references.
  const refPattern = /[a-zA-Z_][\w]*/g
  const identMatches = expr.match(refPattern) ?? []
  const uniqueIdents = Array.from(new Set(identMatches))
  for (const ident of uniqueIdents) {
    const raw = ctx.identifiers[ident]
    if (raw == null || raw === "") {
      return {
        value: null,
        error: translate("QMS/FORMULA/ERR_MISSING_VALUE", { ident }),
      }
    }
    const num = Number(String(raw).replace(/,/g, "."))
    if (isNaN(num)) {
      return {
        value: null,
        error: translate("QMS/FORMULA/ERR_NON_NUMERIC_VALUE", {
          ident,
          raw: String(raw),
        }),
      }
    }
    // Use word-boundary replace so `menge_me` doesn't clobber `menge`.
    expr = expr.replace(new RegExp(`\\b${ident}\\b`, "g"), String(num))
  }

  // --- Sanity check: only numbers + operators remain.
  if (!/^[\d\s+\-*/().]+$/.test(expr)) {
    return { value: null, error: translate("QMS/FORMULA/ERR_INVALID_CHARS") }
  }

  try {
    const result = Function(`"use strict"; return (${expr})`)()
    if (typeof result !== "number" || !isFinite(result)) {
      return {
        value: null,
        error: translate("QMS/FORMULA/ERR_INVALID_RESULT"),
      }
    }
    const formatted = Number.isInteger(result)
      ? String(result)
      : String(parseFloat(result.toPrecision(10)))
    return { value: formatted, error: null }
  } catch {
    return { value: null, error: translate("QMS/FORMULA/ERR_EVAL_FAILED") }
  }
}

/** Extract the identifiers referenced by a formula (bare + bracket). */
export function extractReferences(expression: string): {
  identifiers: string[]
  docHeaders: string[]
} {
  const docHeaders: string[] = []
  const identifiers: string[] = []
  if (!expression) return { identifiers, docHeaders }

  // [bracket] headers first so we don't also treat their inner text as
  // identifiers in the bare-ident pass below.
  const bracketPattern = /\[([^\]]+)\]/g
  let m: RegExpExecArray | null
  while ((m = bracketPattern.exec(expression)) !== null) {
    docHeaders.push(m[1].trim())
  }
  const without = expression.replace(bracketPattern, " ")

  const refPattern = /[a-zA-Z_][\w]*/g
  const seen = new Set<string>()
  let mm: RegExpExecArray | null
  while ((mm = refPattern.exec(without)) !== null) {
    if (!seen.has(mm[0])) {
      seen.add(mm[0])
      identifiers.push(mm[0])
    }
  }
  return { identifiers, docHeaders }
}

/**
 * Validate that a helper label is a safe bare identifier — lets it be
 * referenced by OTHER helpers via the same substitution pass.
 */
export function isValidHelperLabel(label: string): boolean {
  return /^[a-zA-Z_][\w]*$/.test(label)
}
